/**
 * The Guidelines & policy tab's two documents - `/api/v1/guidelines/` and
 * `/api/v1/policy/` - read on open, published on Save, and put back to the
 * system default on Reset.
 *
 * One contract behind both: every call answers with the same document body,
 * so one mapping serves the lot. The two differ in one thing, and it is
 * enforced here rather than left to the caller: the guidelines are sent
 * without a `clauses` key at all - the server refuses its mere presence -
 * and the policy is sent with its whole list.
 *
 * A publish is a full replace. The whole body goes every time, and for the
 * policy the whole clause list with it; there is nothing to patch, because
 * every publish is a new version and the old one's clauses stay with the
 * old one. What the document looks like once stored is what comes back -
 * the server sanitises the body on the way in, and the editor has to show
 * what was actually kept.
 */

import { ApiError, api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  PolicyClause,
  PolicyDocument,
  PolicyDocumentId,
  PracticeDocumentClauseRequest,
  PracticeDocumentResponse,
  PublishGuidelinesRequest,
  PublishPolicyRequest,
} from "../types/brand.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** Where each document lives, and where its reset is. */
const PATHS: Record<PolicyDocumentId, { document: string; reset: string }> = {
  guidelines: {
    document: API_ENDPOINTS.practiceDocuments.guidelines,
    reset: API_ENDPOINTS.practiceDocuments.guidelinesReset,
  },
  policy: {
    document: API_ENDPOINTS.practiceDocuments.policy,
    reset: API_ENDPOINTS.practiceDocuments.policyReset,
  },
};

/** How the two are named to a person, in an error. */
const NAMES: Record<PolicyDocumentId, string> = {
  guidelines: "your guidelines",
  policy: "your policy",
};

/**
 * A `{{clause:N}}` token, as the body writes one. The whole body is searched
 * with it, so the flag is global - and a fresh regex each time, since a
 * global one keeps its place between calls.
 */
const clauseToken = () => /\{\{clause:(\d+)\}\}/g;

/** The positions the body names, each once. */
export const referencedPositions = (body: string): Set<number> => {
  const positions = new Set<number>();
  const pattern = clauseToken();
  let match = pattern.exec(body);
  while (match) {
    positions.add(Number(match[1]));
    match = pattern.exec(body);
  }
  return positions;
};

/**
 * The clauses the body still names. A publish is refused whole if a clause
 * is sent whose token is nowhere in the body, and the one way that happens
 * is a chip deleted in the editor - which is the practitioner saying the
 * clause goes. So the list sent is the list the body asks for; a token
 * with no clause behind it cannot be fixed here and is left for the server
 * to name.
 */
export const referencedClauses = (
  body: string,
  clauses: PolicyClause[]
): PolicyClause[] => {
  const named = referencedPositions(body);
  return clauses.filter((clause) => named.has(clause.position));
};

/**
 * A document as the tab reads it. `clauses` is always a list on the wire,
 * but `?? []` all the same: a row that omitted the key would otherwise put
 * `undefined` where the screen maps over a list.
 */
export const toPolicyDocument = (
  id: PolicyDocumentId,
  response: PracticeDocumentResponse
): PolicyDocument => ({
  id,
  body: response.body,
  resolvedBody: response.resolved_body,
  clauses: (response.clauses ?? []).map((clause) => ({
    label: clause.label,
    duration: clause.package_duration,
    position: clause.position,
  })),
  version: response.version,
  isCustom: response.is_custom,
});

const toClauseRequest = (clause: PolicyClause): PracticeDocumentClauseRequest => ({
  label: clause.label,
  package_duration_id: clause.duration.id,
  position: clause.position,
});

/**
 * The `code` the server put on a refusal - `clause_token_mismatch`,
 * `guidelines_cannot_have_clauses`, `unknown_package_duration` - which is
 * what to branch on, never the message. `undefined` for anything that is
 * not one of the server's envelopes.
 */
export const policyErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  const body = error.data;
  if (!body || typeof body !== "object") return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
};

/**
 * A `clause_token_mismatch` names the field at fault under `details` - the
 * body, for a token with no clause; the clauses, for the other way round
 * or a position claimed twice. That line is the one worth showing, so the
 * error is handed on carrying it as its message. Anything else goes back
 * as it came.
 */
const withClauseDetail = (error: unknown): unknown => {
  if (policyErrorCode(error) !== "clause_token_mismatch") return error;
  const { data, status } = error as ApiError;
  const details = (data as { details?: Record<string, unknown> }).details;
  const line = details?.body ?? details?.clauses;
  return typeof line === "string" && line.trim()
    ? new ApiError(line.trim(), status, data)
    : error;
};

/** The document as it stands - custom, or the system default. */
export async function fetchPolicyDocument(
  getToken: GetToken,
  id: PolicyDocumentId,
  signal?: AbortSignal
): Promise<PolicyDocument> {
  const response = await api.get<PracticeDocumentResponse>(
    PATHS[id].document,
    { token: await token(getToken, `load ${NAMES[id]}`), signal }
  );
  return toPolicyDocument(id, response);
}

/**
 * Publishes a new version: the whole body, and for the policy the clauses
 * the body still names. Answers with the version as stored.
 */
export async function publishPolicyDocument(
  getToken: GetToken,
  id: PolicyDocumentId,
  body: string,
  clauses: PolicyClause[],
  signal?: AbortSignal
): Promise<PolicyDocument> {
  // No `clauses` key on the guidelines, not even an empty one.
  const request: PublishGuidelinesRequest | PublishPolicyRequest =
    id === "guidelines"
      ? { body }
      : { body, clauses: referencedClauses(body, clauses).map(toClauseRequest) };

  try {
    const response = await api.put<PracticeDocumentResponse>(
      PATHS[id].document,
      request,
      { token: await token(getToken, `save ${NAMES[id]}`), signal }
    );
    return toPolicyDocument(id, response);
  } catch (error) {
    throw withClauseDetail(error);
  }
}

/**
 * Puts the system default back, and answers with it. Fine to ask for on a
 * document already on the default - the server says `200` either way.
 */
export async function resetPolicyDocument(
  getToken: GetToken,
  id: PolicyDocumentId,
  signal?: AbortSignal
): Promise<PolicyDocument> {
  const response = await api.post<PracticeDocumentResponse>(
    PATHS[id].reset,
    undefined,
    { token: await token(getToken, `reset ${NAMES[id]}`), signal }
  );
  return toPolicyDocument(id, response);
}
